// client/src/components/auth/SubscriptionPage.js
import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/auth/AuthProvider';
import { isFranquiaAdmin } from '../../utils/permissions';
import { useContaPlans } from '../../hooks/useContaPlans';
import { useFranquiaPlans } from '../../hooks/useFranquiaPlans';
import { useMutateSubscriptions } from '../../hooks/useSubscriptions';
import { useMutateFranquiaSubscriptions } from '../../hooks/useFranquiaSubscriptions';
import FormModal from '../layout/FormModal';
import {
    Loader2, Zap, CheckCircle, Package, ChevronDown, Check,
    CreditCard, AlertCircle, MapPin, CheckCircle2
} from 'lucide-react';

const featureLabels = {
    hasAgenda: 'Agenda Completa', hasRecursos: 'Gestão de Recursos', hasServicos: 'Cadastro de Serviços',
    hasFornecedores: 'Cadastro de Fornecedores', hasItensVenda: 'Gestão de Itens de Venda',
    hasPacotes: 'Criação de Pacotes', hasContratos: 'Gestão de Contratos', hasTiposContrato: 'Modelos de Contrato',
    hasTemplates: 'Templates Personalizados', hasGaleria: 'Galeria de Fotos', hasTransportadores: 'Gestão de Transportadores',
    hasTemplateAnamnese: 'Templates de Anamnese', hasTemplateCertificado: 'Templates de Certificado',
    hasTemplateContrato: 'Templates de Contrato', hasTemplateRecibo: 'Templates de Recibo',
    limiteUsuarios: 'Usuários', limiteClientes: 'Clientes', limiteFornecedores: 'Fornecedores',
    limiteServicos: 'Serviços', limiteRecursos: 'Recursos', limiteEnderecos: 'Endereços',
    limiteItensVenda: 'Itens de Venda', limitePacotes: 'Pacotes', limiteContratos: 'Contratos',
    limiteTiposContrato: 'Tipos de Contrato', limiteTemplates: 'Templates (Total)',
    limiteTransportadores: 'Transportadores', maxStorageGB: 'Armazenamento (GB)',
    maxAccounts: 'Máximo de Contas', matrizFeePercentage: 'Taxa da Matriz'
};

const PlanCard = ({ plan, isSelected, onSelect, isCurrentPlan }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const isFranquiaPlan = plan.features.maxAccounts !== undefined;

    const formatLimit = (limitObj) => {
        if (!limitObj) return 'N/A';
        if (limitObj.isUnlimited || limitObj.value === -1) return 'Ilimitado';
        return limitObj.value;
    };

    const topFeatures = isFranquiaPlan
        ? [
            // ✅ maxAccounts já vem como { isUnlimited, value } (LimitSchema),
            // igual limiteUsuarios/limiteClientes abaixo — envolver de novo em
            // { value: ... } fazia formatLimit devolver o objeto inteiro,
            // renderizando "[object Object]" na tela.
            `Até ${formatLimit(plan.features.maxAccounts)} contas`,
            `Taxa da Matriz: ${plan.features.matrizFeePercentage}%`
        ]
        : [
            `Até ${formatLimit(plan.features.limiteUsuarios)} Usuários`,
            `Até ${formatLimit(plan.features.limiteClientes)} Clientes`,
            `${plan.features.maxStorageGB} GB de Armazenamento`
        ];

    const allFeatures = useMemo(() => {
        if (!plan?.features) return [];
        return Object.entries(featureLabels)
            .filter(([key]) => plan.features.hasOwnProperty(key))
            .map(([key, label]) => {
                const value = plan.features[key];
                if (typeof value === 'boolean') {
                    return { label, included: value };
                } else if (typeof value === 'object' && value !== null) {
                    return { label, value: formatLimit(value) };
                } else {
                    return { label, value };
                }
            });
    }, [plan.features]);

    return (
        <div
            className={`plan-card ${isSelected ? 'selected' : ''} ${isCurrentPlan ? 'current' : ''}`}
            onClick={() => onSelect(plan)}
            aria-label={`Plano ${plan.name}`}
        >
            {isCurrentPlan && <div className="current-plan-badge">Plano Atual</div>}
            <div className="plan-card-main-content">
                <div className="plan-card-header">
                    <h3>{plan.name}</h3>
                    {isSelected && <CheckCircle className="selected-icon" size={24} />}
                </div>
                <div className="plan-card-price">
                    {(plan.price / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                    <span>/mês</span>
                </div>
                <ul className="plan-card-features">
                    {topFeatures.map((feature, index) => (
                        <li key={index}>{feature}</li>
                    ))}
                </ul>
            </div>
            <div className="plan-card-expander">
                <button
                    onClick={(e) => { e.stopPropagation(); setIsExpanded(!isExpanded); }}
                    className="expand-button"
                    aria-expanded={isExpanded}
                >
                    <span>Ver todos os recursos</span>
                    <ChevronDown size={16} className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                </button>
                {isExpanded && (
                    <div className="expanded-content">
                        <table className="feature-table">
                            <thead>
                                <tr><th>Recurso</th><th>Limite</th></tr>
                            </thead>
                            <tbody>
                                {allFeatures.map((feature, index) => (
                                    feature.included !== false && (
                                        <tr key={index}>
                                            <td>{feature.label}</td>
                                            <td>
                                                {feature.included === true
                                                    ? <Check className="icon-check" />
                                                    : <strong>{feature.value}</strong>
                                                }
                                            </td>
                                        </tr>
                                    )
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

const PaymentForm = ({ onSubmit, onCancel, isProcessing, selectedPlan, error, entity }) => {
    const [cardData, setCardData] = useState({
        number: '', holder_name: '', exp_month: '', exp_year: '', cvv: ''
    });
    const [addressOption, setAddressOption] = useState('existing');
    const [selectedAddressId, setSelectedAddressId] = useState('');
    const [newAddress, setNewAddress] = useState({
        cep: '', logradouro: '', numero: '', complemento: '', bairro: '', localidade: '', uf: ''
    });
    const [paymentError, setPaymentError] = useState(error || '');
    const [isCepLoading, setIsCepLoading] = useState(false);
    const [cepError, setCepError] = useState('');
    const [cepEncontrado, setCepEncontrado] = useState(false);

    const availableAddresses = useMemo(() => entity?.enderecos || [], [entity]);

    useEffect(() => {
        setPaymentError(error || '');
    }, [error]);

    useEffect(() => {
        if (availableAddresses.length > 0) {
            setAddressOption('existing');
            setSelectedAddressId(availableAddresses[0]._id);
        } else {
            setAddressOption('new');
        }
    }, [availableAddresses]);

    const handleCardInputChange = (e) => {
        let { name, value } = e.target;
        if (name === 'number') {
            const digits = value.replace(/\D/g, '').substring(0, 16);
            value = digits.replace(/(\d{4})/g, '$1 ').trim();
        } else if (['exp_month', 'exp_year', 'cvv'].includes(name)) {
            value = value.replace(/\D/g, '');
        } else if (name === 'holder_name') {
            value = value.replace(/[^A-Za-zÀ-ÿ\s]/g, '');
        }
        setCardData(prev => ({ ...prev, [name]: value }));
    };

    const handleAddressInputChange = (e) => {
        const { name, value } = e.target;
        if (name === 'cep') { setCepError(''); setCepEncontrado(false); }
        setNewAddress(prev => ({ ...prev, [name]: value }));
    };

    const handleCepBlur = async (e) => {
        const cep = e.target.value.replace(/\D/g, '');
        if (cep.length !== 8) {
            if (cep.length > 0) setCepError('CEP inválido.');
            return;
        }
        setIsCepLoading(true);
        setCepError('');
        try {
            const response = await fetch(`https://api.pagar.me/1/zipcodes/${cep}`);
            if (!response.ok) throw new Error('CEP não encontrado ou inválido.');
            const data = await response.json();
            setNewAddress(prev => ({
                ...prev,
                cep: data.zipcode || '',
                logradouro: data.street || '',
                bairro: data.neighborhood || '',
                localidade: data.city || '',
                uf: data.state || '',
            }));
            setCepEncontrado(true);
        } catch (err) {
            setCepError(err.message || 'Erro ao buscar CEP.');
        } finally {
            setIsCepLoading(false);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setPaymentError('');

        const currentYear = new Date().getFullYear();
        const currentMonth = new Date().getMonth() + 1;
        const expMonth = parseInt(cardData.exp_month, 10);
        let expYear = parseInt(cardData.exp_year, 10);
        if (isNaN(expMonth) || expMonth < 1 || expMonth > 12) {
            return setPaymentError('Mês de validade do cartão é inválido (use 01-12).');
        }
        if (expYear < 100) expYear += 2000;
        if (expYear < currentYear || (expYear === currentYear && expMonth < currentMonth)) {
            return setPaymentError('Data de validade do cartão está no passado.');
        }
        if (cardData.cvv.length < 3 || cardData.cvv.length > 4) {
            return setPaymentError('CVV inválido.');
        }

        let finalBillingAddress = null;
        if (addressOption === 'existing') {
            if (!selectedAddressId) {
                return setPaymentError('Por favor, selecione um endereço de cobrança.');
            }
            finalBillingAddress = availableAddresses.find(addr => addr._id === selectedAddressId);
        } else {
            if (!newAddress.cep || !newAddress.logradouro || !newAddress.numero || !newAddress.bairro || !newAddress.localidade || !newAddress.uf) {
                return setPaymentError('Por favor, preencha todos os campos do novo endereço.');
            }
            finalBillingAddress = newAddress;
        }

        if (!finalBillingAddress) {
            return setPaymentError('Endereço de cobrança inválido.');
        }

        onSubmit(cardData, finalBillingAddress);
    };

    return (
        <FormModal
            title={<div className="flex items-center gap-2"><CreditCard size={20} /><span>Pagamento - {selectedPlan.name}</span></div>}
            onClose={onCancel}
            size="large"
        >
            <div className="payment-form-content">
                {paymentError && (
                    <div className="error-message mb-4">
                        <AlertCircle size={20} />
                        <span>{paymentError}</span>
                    </div>
                )}
                <form onSubmit={handleSubmit}>
                    <div className="form-grid">
                        <div className="form-group full-width">
                            <label><MapPin size={14} className="inline-block mr-2" /> Endereço de Cobrança</label>
                            {availableAddresses.length > 0 && (
                                <div className='address-options mt-2'>
                                    <label className='radio-label'>
                                        <input type="radio" name="addressOption" value="existing" checked={addressOption === 'existing'} onChange={(e) => setAddressOption(e.target.value)} />
                                        <span>Usar endereço existente</span>
                                    </label>
                                    <label className='radio-label'>
                                        <input type="radio" name="addressOption" value="new" checked={addressOption === 'new'} onChange={(e) => setAddressOption(e.target.value)} />
                                        <span>Adicionar novo endereço</span>
                                    </label>
                                </div>
                            )}
                        </div>

                        {addressOption === 'existing' && availableAddresses.length > 0 && (
                            <div className="form-group full-width">
                                <select name="selectedAddressId" value={selectedAddressId} onChange={e => setSelectedAddressId(e.target.value)} className="form-input">
                                    {availableAddresses.map(addr => (
                                        <option key={addr._id} value={addr._id}>
                                            {`${addr.logradouro}, ${addr.numero} - ${addr.bairro}, ${addr.localidade} - ${addr.uf}`}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </div>

                    {addressOption === 'new' && (
                        <div className='form-grid'>
                            <div className="form-group">
                                <label>CEP</label>
                                <input type="text" name="cep" value={newAddress.cep} onChange={handleAddressInputChange} onBlur={handleCepBlur} className="form-input" required disabled={isProcessing || isCepLoading} />
                                {cepError && <small className='error-text'>{cepError}</small>}
                                {cepEncontrado ? (
                                    <div className="form-hint" style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                                        <CheckCircle2 size={13} style={{ color: 'var(--color-success, #16a34a)', flexShrink: 0 }} />
                                        Endereço preenchido automaticamente.
                                        <button type="button" onClick={() => setCepEncontrado(false)}
                                            style={{ background: 'none', border: 'none', padding: 0, color: 'var(--color-primary)', cursor: 'pointer', fontSize: 'inherit', textDecoration: 'underline' }}>
                                            Editar manualmente
                                        </button>
                                    </div>
                                ) : (
                                    <div className="form-hint">Digite o CEP e aguarde o preenchimento automático</div>
                                )}
                            </div>
                            <div className="form-group"><label>Logradouro</label><input type="text" name="logradouro" value={newAddress.logradouro} onChange={handleAddressInputChange} className="form-input" required disabled={isProcessing}
                                readOnly={cepEncontrado} style={cepEncontrado ? { backgroundColor: 'var(--color-bg-subtle, #f1f5f9)', cursor: 'not-allowed' } : undefined} /></div>
                            <div className="form-group"><label>Número</label><input type="text" name="numero" value={newAddress.numero} onChange={handleAddressInputChange} className="form-input" required disabled={isProcessing} /></div>
                            <div className="form-group"><label>Complemento</label><input type="text" name="complemento" value={newAddress.complemento} onChange={handleAddressInputChange} className="form-input" disabled={isProcessing} /></div>
                            <div className="form-group"><label>Bairro</label><input type="text" name="bairro" value={newAddress.bairro} onChange={handleAddressInputChange} className="form-input" required disabled={isProcessing}
                                readOnly={cepEncontrado} style={cepEncontrado ? { backgroundColor: 'var(--color-bg-subtle, #f1f5f9)', cursor: 'not-allowed' } : undefined} /></div>
                            <div className="form-group"><label>Cidade</label><input type="text" name="localidade" value={newAddress.localidade} onChange={handleAddressInputChange} className="form-input" required disabled={isProcessing}
                                readOnly={cepEncontrado} style={cepEncontrado ? { backgroundColor: 'var(--color-bg-subtle, #f1f5f9)', cursor: 'not-allowed' } : undefined} /></div>
                            <div className="form-group"><label>UF</label><input type="text" name="uf" value={newAddress.uf} onChange={handleAddressInputChange} className="form-input" required maxLength="2" disabled={isProcessing}
                                readOnly={cepEncontrado} style={cepEncontrado ? { backgroundColor: 'var(--color-bg-subtle, #f1f5f9)', cursor: 'not-allowed' } : undefined} /></div>
                        </div>
                    )}
                    
                    <div className="form-grid">
                        <div className="form-group full-width">
                            <label>Nome no Cartão</label>
                            <input type="text" name="holder_name" value={cardData.holder_name} onChange={handleCardInputChange} className="form-input" required placeholder="Como escrito no cartão" disabled={isProcessing}/>
                        </div>
                        <div className="form-group full-width">
                            <label>Número do Cartão</label>
                            <input type="text" name="number" value={cardData.number} onChange={handleCardInputChange} className="form-input" required placeholder="0000 0000 0000 0000" disabled={isProcessing}/>
                        </div>
                        <div className="form-group">
                            <label>Mês de Validade (MM)</label>
                            <input type="text" name="exp_month" value={cardData.exp_month} onChange={handleCardInputChange} className="form-input" placeholder="MM" required maxLength="2" disabled={isProcessing}/>
                        </div>
                        <div className="form-group">
                            <label>Ano de Validade (AA ou AAAA)</label>
                            <input type="text" name="exp_year" value={cardData.exp_year} onChange={handleCardInputChange} className="form-input" placeholder="AA ou AAAA" required maxLength="4" disabled={isProcessing}/>
                        </div>
                        <div className="form-group">
                            <label>CVV</label>
                            <input type="text" name="cvv" value={cardData.cvv} onChange={handleCardInputChange} className="form-input" required maxLength="4" disabled={isProcessing}/>
                        </div>
                    </div>
                    <div className="payment-actions">
                        <button type="button" onClick={onCancel} className="btn-secondary" disabled={isProcessing}>Cancelar</button>
                        <button type="submit" className="btn-primary" disabled={isProcessing}>
                            {isProcessing ? <><Loader2 className="animate-spin" size={16} /> Processando...</> : <><Zap size={16} /> Confirmar Pagamento</>}
                        </button>
                    </div>
                </form>
                <div className="payment-security-note"><Check size={16} /><span>Seus dados são criptografados e seguros</span></div>
            </div>
        </FormModal>
    );
};

// SubscriptionPage
const SubscriptionPage = () => {
    const navigate = useNavigate();
    const { user, hasPermission } = useAuth();
    const [isAllowed, setIsAllowed] = useState(true);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [isExpired, setIsExpired] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(null);

    // ✅ role.name guarda o nome de exibição do banco (ex.: "Administrador
    // Franquia"), nunca o slug 'franquia_admin' — essa comparação nunca era
    // verdadeira, então todo franquia_admin caía incorretamente no fluxo de
    // planos/assinatura de Conta. isFranquiaAdmin() é o utilitário correto,
    // já usado no resto do app (ex.: Sidebar.js), baseado em role.scope.
    const isFranquiaUser = isFranquiaAdmin(user);

    const { data: plansConta = [], isLoading: contaPlansLoading, error: contaPlansError } =
        useContaPlans({ status: 'active' }, { enabled: !isFranquiaUser && isAllowed });
    const { data: plansFranquia = [], isLoading: franquiaPlansLoading, error: franquiaPlansError } =
        useFranquiaPlans({ status: 'active' }, { enabled: isFranquiaUser && isAllowed });

    const {
        createContaSubscription, changePlan: changeContaPlan, cancelSubscription: cancelContaSubscription,
        isLoading: isSubscriptionLoading, error: subscriptionError
    } = useMutateSubscriptions();

    const {
        createFranquiaSubscription, changePlan: changeFranquiaPlan, cancelSubscription: cancelFranquiaSubscription,
        isLoading: isFranquiaSubscriptionLoading, error: franquiaSubscriptionError
    } = useMutateFranquiaSubscriptions();

    const isLoading = contaPlansLoading || franquiaPlansLoading;
    const isProcessing = isSubscriptionLoading || isFranquiaSubscriptionLoading;

    const plansError = contaPlansError || franquiaPlansError;
    const mutationError = subscriptionError || franquiaSubscriptionError;

    const plans = isFranquiaUser ? plansFranquia : plansConta;
    const entity = isFranquiaUser ? user?.franquia : user?.account;
    const currentPlanId = entity?.plano?._id || entity?.plano;

    const subscription = entity?.subscription || entity?.franquiasSubscription;
    const hasActiveSubscription = entity && ['active', 'trialing'].includes(entity.status);

    useEffect(() => {
        // ✅ CORREÇÃO APLICADA AQUI:
        // Verifica se o usuário tem permissão para gerenciar assinaturas,
        // seja para contas ou para franquias.
        const canAccessContaSubscription = !isFranquiaUser && hasPermission('subscription_conta:manage');
        const canAccessFranquiaSubscription = isFranquiaUser && hasPermission('franchise_subscription:manage');

        if (user && (!canAccessContaSubscription && !canAccessFranquiaSubscription)) {
            setIsAllowed(false);
            navigate('/');
        }
    }, [user, hasPermission, navigate, isFranquiaUser]); // Adicionado isFranquiaUser às dependências do useEffect

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const expiredParam = urlParams.get('expired');
        setIsExpired(expiredParam === 'true');
        if (plansError) setErrorMessage('Erro ao carregar planos. Tente novamente mais tarde.');
        if (mutationError) setErrorMessage(mutationError.message || 'Erro ao processar assinatura.');
    }, [plansError, mutationError]);

    useEffect(() => {
        if (currentPlanId && plans.length > 0 && !selectedPlan) {
            const currentPlan = plans.find(plan => plan._id === currentPlanId);
            if (currentPlan) setSelectedPlan(currentPlan);
        }
    }, [currentPlanId, plans, selectedPlan]);

    const handlePaymentSubmit = async (cardData, billingAddress) => {
        if (!selectedPlan) {
            setErrorMessage('Nenhum plano selecionado.');
            return;
        }
        if (!billingAddress) {
            setErrorMessage('Endereço de cobrança é obrigatório.');
            return;
        }
        setErrorMessage('');

        try {
            // ✅ LÓGICA CORRIGIDA: Esta função agora trata tanto a criação quanto a troca de plano.
            if (hasActiveSubscription) {
                if (isFranquiaUser) {
                    await changeFranquiaPlan({
                        subscriptionId: subscription._id,
                        newPlanId: selectedPlan._id,
                        cardData, // 'cardData' pode ser necessário se o cartão anterior falhar ou não existir.
                        billingAddress
                    });
                } else {
                    await changeContaPlan({
                        newPlanId: selectedPlan._id,
                        cardData,
                        billingAddress
                    });
                }
            } else {
                if (isFranquiaUser) {
                    await createFranquiaSubscription({ planId: selectedPlan._id, cardData, franquiaId: entity._id, billingAddress });
                } else {
                    await createContaSubscription({ planId: selectedPlan._id, cardData, billingAddress });
                }
            }
            setShowPaymentModal(false);
        } catch (error) {
            console.error('Erro ao processar pagamento no componente:', error);
            setErrorMessage(error.message || 'Não foi possível processar o pagamento.');
        }
    };

    // ✅ LÓGICA CORRIGIDA: Esta função decide se abre o modal ou troca o plano diretamente.
    const handleActionButtonClick = async () => {
        if (!selectedPlan) return;
    
        if (hasActiveSubscription) {
            // Se já tem assinatura, troca o plano diretamente, sem pedir cartão.
            const confirmation = window.confirm(`Tem certeza que deseja alterar seu plano para ${selectedPlan.name}? A cobrança com o novo valor será feita no próximo ciclo.`);
            if (confirmation) {
                setErrorMessage('');
                try {
                    if (isFranquiaUser) {
                        await changeFranquiaPlan({
                            subscriptionId: subscription._id,
                            newPlanId: selectedPlan._id,
                            // Não passamos cardData. O endereço pode ser opcional no backend para trocas.
                        });
                    } else {
                        await changeContaPlan({
                            newPlanId: selectedPlan._id,
                        });
                    }
                } catch (error) {
                    console.error('Erro ao trocar de plano:', error);
                    setErrorMessage(error.message || 'Não foi possível trocar o plano.');
                }
            }
        } else {
            // Se não tem assinatura, abre o modal de pagamento.
            setShowPaymentModal(true);
        }
    };

    const handleCancelSubscription = async (immediate = false) => {
        if (!subscription) {
            setErrorMessage("Não foi possível encontrar os dados da sua assinatura atual para o cancelamento. Por favor, recarregue a página.");
            return;
        }

        try {
            if (isFranquiaUser) {
                await cancelFranquiaSubscription({ subscriptionId: subscription._id, immediate });
            } else {
                await cancelContaSubscription({ immediate });
            }
        } catch (error) {
            console.error('Erro ao cancelar assinatura:', error);
            if (error.message !== 'Ação cancelada pelo usuário') {
                setErrorMessage(error.message || 'Não foi possível cancelar a assinatura.');
            }
        }
    };

    const handlePlanSelection = (plan) => {
        setSelectedPlan(plan);
        setErrorMessage('');
    };

    if (!isAllowed) return null;
    if (isLoading || !user) {
        return (
            <div className="page-management-container">
                <div className="content-panel mt-6">
                    <div className="p-8 text-center">
                        <Loader2 className="animate-spin mx-auto" /> Carregando...
                    </div>
                </div>
            </div>
        );
    }

    // ✅ Mostra todo plano ativo selecionável — só exclui o trial padrão
    // (isDefaultTrial), que é atribuído automaticamente e não deve aparecer
    // pra escolha manual aqui. Antes filtrava por price > 0, o que também
    // escondia planos reais e ativos de preço zero (ex.: Landing Page, Loja
    // Básico, Loja Avançado).
    const availablePlans = plans.filter(p => !p.isDefaultTrial);
    const shouldShowPaymentButton = selectedPlan && (selectedPlan._id !== currentPlanId || isExpired);

    return (
        <div className="page-management-container">
            <div className="page-header">
                <h1 className="page-title">
                    <Package className="icon-title" /> {hasActiveSubscription ? 'Meu Plano e Assinatura' : 'Assinar um Plano'}
                </h1>
            </div>
            <div className="content-panel mt-6">
                <div className="form-section">
                    {errorMessage && (
                        <div className="error-message">
                            <AlertCircle size={20} />
                            <span>{errorMessage}</span>
                        </div>
                    )}

                    {!hasActiveSubscription && (
                        <>
                            <legend className="form-section-title">
                                {isExpired ? 'Sua assinatura expirou!' : 'Seu período de teste terminou!'}
                            </legend>
                            <p className="form-hint mb-6">
                                {isExpired
                                    ? 'Sua assinatura expirou. Por favor, escolha um plano para continuar usando o sistema.'
                                    : 'Escolha um dos nossos planos para continuar aproveitando todos os recursos.'}
                            </p>
                        </>
                    )}

                    {hasActiveSubscription && entity?.plano && (
                        <div className="current-plan-info">
                            <h4>Seu Plano Atual</h4>
                            <p>
                                Você está no <strong>{entity.plano.name}</strong>.{" "}
                                {subscription?.status === "pending_cancellation" && subscription?.currentPeriodEnd ? (
                                    <>Seu cancelamento já está agendado para <strong>{new Date(subscription.currentPeriodEnd).toLocaleDateString("pt-BR")}</strong>.</>
                                ) : subscription?.status === "canceled" && subscription?.canceledAt ? (
                                    <>Sua assinatura foi cancelada em <strong>{new Date(subscription.canceledAt).toLocaleDateString("pt-BR")}</strong>.</>
                                ) : subscription?.currentPeriodEnd ? (
                                    <>Sua próxima cobrança será em {new Date(subscription.currentPeriodEnd).toLocaleDateString("pt-BR")}.</>
                                ) : (
                                    "Sua assinatura está sendo processada."
                                )}
                            </p>
                            <p className="form-hint">
                                Para trocar de plano, selecione um novo plano abaixo e confirme a alteração.
                            </p>

                            {subscription?.status !== "pending_cancellation" && (
                                <button onClick={() => handleCancelSubscription(false)} className="btn-danger-outline mt-4">
                                    Cancelar Assinatura (ao final do período)
                                </button>
                            )}

                            {subscription?.status === "pending_cancellation" && (
                                <button onClick={() => handleCancelSubscription(true)} className="btn-danger mt-4">
                                    Cancelar Imediatamente
                                </button>
                            )}
                        </div>
                    )}

                    <div className="plans-list grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
                        {availablePlans.map((plan) => (
                            <PlanCard
                                key={plan._id}
                                plan={plan}
                                isSelected={selectedPlan?._id === plan._id}
                                onSelect={handlePlanSelection}
                                isCurrentPlan={currentPlanId === plan._id}
                            />
                        ))}
                    </div>

                    {shouldShowPaymentButton && (
                        <div className="actions mt-6">
                            <button
                                onClick={handleActionButtonClick} // ✅ CORREÇÃO: Chama a nova função de clique
                                className="btn-primary"
                                disabled={isProcessing}
                            >
                                {isProcessing
                                    ? <><Loader2 className="animate-spin mr-2" size={16} /> Processando...</>
                                    : <><Zap className="mr-2" size={16} /> {hasActiveSubscription ? `Alterar para ${selectedPlan?.name}` : `Assinar ${selectedPlan?.name}`}</>
                                }
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {showPaymentModal && selectedPlan && (
                <PaymentForm
                    selectedPlan={selectedPlan}
                    onSubmit={handlePaymentSubmit}
                    onCancel={() => setShowPaymentModal(false)}
                    isProcessing={isProcessing}
                    error={errorMessage}
                    entity={entity}
                />
            )}
        </div>
    );
};

export default SubscriptionPage;